import {PolymerElement, html} from '@polymer/polymer';
import {ScopeBinding, ScopeRoot} from '../behaviors/keli-binding-behavior';
import '@polymer/polymer/lib/elements/dom-if.js';
import {afterNextRender} from '@polymer/polymer/lib/utils/render-status.js';
import {microTask} from '@polymer/polymer/lib/utils/async.js';

class KeliPanelController extends ScopeRoot(ScopeBinding(PolymerElement)) {
  static get template() {
    return html`
      <keli-panel id="_t" keliname="_t">
        <template is="dom-if" if="[[contentPlaceholder]]">
          <keli-panel-content-placeholder id="placeholder" slot="placeholder" error-timeout$="[[contentPlaceholderErrorTimeout]]"></keli-panel-content-placeholder>
        </template>
        <keli-panel-toolbar keliname="toolbar" slot="toolbar"></keli-panel-toolbar>
        <keli-panel-fields id="fields" keliname="editobjects" slot="fields"></keli-panel-fields>
        <keli-panel-browse keliname="browseobjects" slot="browse"></keli-panel-browse>
        <keli-panel-sidebar keliname="sidebar" slot="sidebar"></keli-panel-sidebar>
        <keli-panel-full keliname="fullpanel" slot="full"></keli-panel-full>
        <keli-panel-footer keliname="footerbar" slot="footer"></keli-panel-footer>
      </keli-panel>`;
  }

  static get is() {
    return 'keli-panel-controller'
  }

  static get properties() {
    return {
      contentPlaceholder: {type: Boolean},
      contentPlaceholderErrorTimeout: {type: Number},
      disableBrowseConfiguration: {type: Boolean},
      disableControlConfiguration: {type: Boolean},
      disablePanelMenu: {type: Boolean},
      id: {type: String, reflectToAttribute: true},
      manualKeliReady: {type: Boolean, value: true}
    }
  }

  constructor() {
    super();
    this.addEmitToKeli([
      'keliPanelOpened',
      'controlsLoaded',
      'allDataLoaded',
      'keliClosePanel',
      'openPanelAttachments',
      'panelControlCreate',
      'panelControlDelete',
      'panelControlHelp'
    ]);
    this.addOnKeli({
      'deferredChangesFinished': 'clearContentPlaceholder',
      'uiClosePanel': 'handleClosePanel',
      'uiClosePanelStopped': 'handleClosePanelStopped',
      'uiFocus': 'handleFocus',
      'uiObjectVisChanged': 'handleControlVisibilityChange',
      'uiSaveAll': 'saveAllFields'
    });
  }

  connectedCallback() {
    super.connectedCallback();

    if (this.disableControlConfiguration) {
      this.$._t.disableControlConfiguration = this.disableControlConfiguration;
    }
    if (this.disablePanelMenu) {
      this.$._t.disablePanelMenu = this.disablePanelMenu;
    }

    this.addEventListener('closePanel', this.handleClosePanelRequest);
    this.addEventListener('dataLoaded', this.handleDataLoaded);
    this.addEventListener('keliControlMenuReady', this.handleKeliControlMenuReady);
    this.addEventListener('keliControlObjectVisChanged', this.handleKeliControlObjectVisChanged);
    this.addEventListener('keliOverrideScopeVar', this.handleKeliOverrideScopeVar);
    this.addEventListener('keliPanelObjectVisibilityResetAll', this.handleKeliPanelObjectVisibilityResetAll);
    this.addEventListener('keliPanelObjectVisibilityToggled', this.handleKeliPanelObjectVisibilityToggled);
    this.addEventListener('keliUserChange', this.clearContentPlaceholder);
  }

  disconnectedCallback() {
    super.disconnectedCallback();

    this.removeEventListener('closePanel', this.handleClosePanelRequest);
    this.removeEventListener('dataLoaded', this.handleDataLoaded);
    this.removeEventListener('keliControlMenuReady', this.handleKeliControlMenuReady);
    this.removeEventListener('keliControlObjectVisChanged', this.handleKeliControlObjectVisChanged);
    this.removeEventListener('keliOverrideScopeVar', this.handleKeliOverrideScopeVar);
    this.removeEventListener('keliPanelObjectVisibilityResetAll', this.handleKeliPanelObjectVisibilityResetAll);
    this.removeEventListener('keliPanelObjectVisibilityToggled', this.handleKeliPanelObjectVisibilityToggled);
    this.removeEventListener('keliUserChange', this.clearContentPlaceholder);

    for (let key in this._combinedMeta) {
      if (!this._combinedMeta.hasOwnProperty(key)) {
        continue;
      }
      if (this._combinedMeta[key] && this._combinedMeta[key].control && this._combinedMeta[key].control.element) {
        this._combinedMeta[key].control.element = null;
      }
      this._combinedMeta[key] = null;
    }
    this._combinedMeta = null;
    this._regions = null;
    this._needsDataLoadedElements = undefined;
    this._needsAllDataLoadedEvent = undefined;
    this._pendingControlVisibilityChanges = undefined;
    this._pendingControlVisibilityChangesBeforeCreation = undefined;
  }

  async keliReady(scope, templateInstance) {
    super.keliReady && await super.keliReady(scope, templateInstance);

    this._combinedMeta = {};
    this._regions = {};
    this._automaticSectionCounter = 0;
    this._needsDataLoadedElements = new Set([this]);
    this._needsAllDataLoadedEvent = new Set();
    this._pendingControlVisibilityChanges = [];
    this._pendingControlVisibilityChangesBeforeCreation = {};

    this.executeOnKeliReadyComplete(async () => {
      this.registerDefaultPanelRegions();

      await this.createInitialControls(templateInstance);
      if (!this.isConnected) return;

      afterNextRender(this, () => {
        this.$._t.initialControlsCreated = true;
        this.$._t.assignControlActivationKeys();

        this.dispatchEvent(new CustomEvent('controlsLoaded', {detail: {element: this, timestamp: Date.now()}}));
        this.dispatchEvent(new CustomEvent('dataLoaded', {detail: {element: this}}));

        afterNextRender(this, async () => {
          if (!this.contentPlaceholder) {
            this.$._t.dispatchEvent(new CustomEvent('focusActiveControl'));
          }
          await this.createMenuControls(templateInstance);
          await this.createRelatedLinksMenuControls(templateInstance);
        });
      });
    });
  }

  async handleClosePanel(event) {
    await this.saveAllFields(event, true);
    this.destroyPanel();
  }

  handleClosePanelStopped() {
    this.$._t.closing = false;
  }

  async handleFocus(event) {
    if (KeliInterface.isTouchDevice || !this.$._t.active) {
      return;
    }

    if (event.skip) {
      if (event.skip < 0) {
        this.$._t.AdjacentElement.reverse(event.skip);
      } else {
        this.$._t.AdjacentElement.forward(event.skip);
      }
      return;
    }
    if (event.index) {
      this.$._t.AdjacentElement.goTo(event.index);
      return;
    }
    if (event.field) {
      afterNextRender(this, () => {
        if (this._combinedMeta && this._combinedMeta[event.field] && this._combinedMeta[event.field].control.element && this._combinedMeta[event.field].control.element.hidden !== true) {
          this._combinedMeta[event.field].control.element.dispatchEvent(new CustomEvent('focusElement', {
            bubbles: true,
            composed: true
          }));
        }
      });
      return;
    }
    this.$._t.dispatchEvent(new CustomEvent('setPanelAsActive', {detail: {element: this.$._t}}));
  }

  async handleControlVisibilityChange(event, templateInstance) {
    if (!event || !event.objectname) {
      return;
    }
    const objectName = event.objectname;
    const visible = !!(event.value || event.value === undefined);

    // If it's a menu item, handle it.
    if (await templateInstance.get(`${objectName}.controltype`) === 'menuitem') {
      await this.updateMenuItem(event, templateInstance, objectName);
      return;
    }

    // Get the metadata for the control
    const meta = this._combinedMeta && this._combinedMeta && this._combinedMeta[objectName];
    if (!meta) {
      this._pendingControlVisibilityChangesBeforeCreation[objectName] = visible;
      return;
    }
    meta.vis = visible;

    // Update the positions of the controls
    if (visible) {
      const controlsMetadata = await this.runKeliFunctionIn(templateInstance, 'getDynamicControls');
      for (const controlMeta of controlsMetadata.list) {
        if (this._combinedMeta[controlMeta.name]) {
          this._combinedMeta[controlMeta.name].index = controlMeta.position || controlMeta.defaultposition;
        }
      }
    }

    // Decide if we need to create, show, or hide the element.
    if (!visible) {
      if (meta.control.element) {
        // Advance the selected field before the field becomes hidden.
        if (this.$._t.activeElement === meta.control.element) {
          this.$._t.AdjacentElement.next(this.$._t.activeElement);
        }
        meta.control.element.hidden = true;
        meta.control.element.style.display = 'none';
        meta.control.element.blur();
        meta.control.element.dispatchEvent(new CustomEvent('panel-controller-visibility', {
          bubbles: true, composed: true
        }));
        this.$._t.$.menu?.toggleRelatedItems(meta.control.element, false);
      }
    } else {
      if (meta.control.element && (meta.control.element.isAttached || meta.control.element.isConnected)) {
        meta.control.element.hidden = false;
        meta.control.element.style.display = '';
        meta.control.element.dispatchEvent(new CustomEvent('panel-controller-visibility', {
          bubbles: true, composed: true
        }));
        this.$._t.$.menu?.toggleRelatedItems(meta.control.element, true);
      } else {
        this._pendingControlVisibilityChanges.push(meta.name);
        this.applyPendingControlVisibilityChanges();
      }
    }
  }

  async handleClosePanelRequest(event) {
    this.$._t.closing = true;
    await this.saveAllFields(event, true);
    this.dispatchEvent(new CustomEvent('keliClosePanel', {
      detail: {
        element: event.detail.element,
        event: event.detail.event
      }
    }));

    if (event.detail.force) {
      this.destroyPanel();
    }
  }

  handleDataLoaded(event) {
    if (!this._needsDataLoadedElements?.size) {
      return;
    }

    this._needsDataLoadedElements.delete(event.detail.element);

    if (this._needsDataLoadedElements.size === 0) {
      if (!this._allDataLoadedSent) {
        this.dispatchEvent(new CustomEvent('allDataLoaded', {detail: {timestamp: Date.now()}}));
        this._allDataLoadedSent = true;
      }
      if (this._needsAllDataLoadedEvent?.size) {
        for (const control of this._needsAllDataLoadedEvent.values()) {
          control.dispatchEvent(new CustomEvent('panelAllDataLoaded'));
          this._needsAllDataLoadedEvent.delete(control);
        }
      }
    }
  }

  handleKeliControlMenuReady(event) {
    if (this.disablePanelMenu) {
      return;
    }
    if (!event.detail || event.detail.length === 0) {
      return;
    }

    const browseMenuItems = [];
    for (let item of event.detail.list) {
      browseMenuItems.push({
        id: item.id,
        keliname: item.name,
        captionPrefix: item.parentCaption,
        menuSection: item.region,
        hotkeys: (item.hotkey && (Array.isArray(item.hotkey) ? item.hotkey : [item.hotkey])),
        keliTargetScopeRoot: item.keliTargetScopeRoot,
        registerToElement: item.registerToElement,
        dependsOnBrowse: item.dependsOnBrowse,
        browseSettled: this.$._t.panelControlsDataSettled[item.dependsOnBrowse]
      });
    }
    this.$._t.$.menu?.updateMenuItems(browseMenuItems);
  }

  async handleKeliControlObjectVisChanged(event) {
    const itemDefinition = [];
    const visible = (event.detail.event.value !== false);
    if (!visible) {
      itemDefinition.push({id: event.detail.id, visible});
    } else {
      const newItemMeta = await event.detail.controller.runKeliFunction('getControlMenuDefinitions', event.detail.keliname);
      if (newItemMeta) {
        itemDefinition.push({
          id: newItemMeta.id,
          keliname: newItemMeta.name,
          menuSection: newItemMeta.region,
          hotkeys: (newItemMeta.hotkey && (Array.isArray(newItemMeta.hotkey) ? newItemMeta.hotkey : [newItemMeta.hotkey])),
          dependsOnBrowse: newItemMeta.dependsonbrowse,
          browseSettled: this.$._t.panelControlsDataSettled[newItemMeta.dependsonbrowse],
          visible,
          captionPrefix: newItemMeta.parentcaption,
          keliTargetScopeRoot: event.detail.controller,
          registerToElement: event.detail.controller.element
        });
      }
    }

    this.$._t.$.menu?.updateMenuItems(itemDefinition);
  }

  handleKeliOverrideScopeVar(event) {
    this.setScopevarOverride(event.detail.scope, event.detail.scopevar, event.detail.value);
    event.stopPropagation();
  }

  async handleKeliPanelObjectVisibilityResetAll() {
    this.$._t.allAvailableControls.forEach((control, index) => {
      control.checked = true;
      this.$._t.set(`allAvailableControls.${index}`, {...control});
    });

    await this.runKeliFunction('clearCurrentView');
  }

  async handleKeliPanelObjectVisibilityToggled(event) {
    const templateInstance = this.getKeliTemplateInstance();
    const options = {p: true, confine: true, view: true};

    await this.runKeliFunctionIn(templateInstance, 'uiSet', `${event.detail.keliname}.vis`, event.detail.toggle, options);
    await this.runKeliFunctionIn(templateInstance, 'uiSet', `${event.detail.keliname}.lazy`, (event.detail.toggle === false ? false : undefined), options);
  }

  applyPendingControlVisibilityChanges() {
    if (!this._pendingControlVisibilityChanges || !this._pendingControlVisibilityChanges.length) {
      return;
    }
    while (this._pendingControlVisibilityChanges.length) {
      const metaName = this._pendingControlVisibilityChanges.shift();
      const meta = this._combinedMeta[metaName];
      if (!meta || meta.control.element) {
        continue;
      }

      meta.control.element = this.createControlElement(meta).element;
      if (meta.control.element) {
        if (meta.control.slot) {
          meta.control.element.setAttribute('slot', meta.control.slot);
        }
        const nextSibling = this.findClosestTrailingElement(meta);
        if (nextSibling) {
          nextSibling.insertAdjacentElement('beforebegin', meta.control.element);
        } else {
          meta.control.regionElement.appendChild(meta.control.element);
        }
      }
    }
    this.$._t.assignControlActivationKeys();
  }

  async clearContentPlaceholder() {
    if (!this.contentPlaceholder) {
      return;
    }
    const placeholder = this.$._t.querySelector('#placeholder');
    if (placeholder) {
      this.contentPlaceholder = false;
      placeholder.destroy();

      if (this.$._t.initialControlsCreated) {
        afterNextRender(this.$._t, () => {
          this.$._t.dispatchEvent(new CustomEvent('focusActiveControl'));
        });
      }
    }
  }

  async createMenuControls(templateInstance) {
    if (this.disablePanelMenu) {
      return;
    }
    const menuMeta = await this.runKeliFunctionIn(templateInstance, 'getMenuMapForUI');

    const initialMenuItems = [];
    if (menuMeta) {
      for (let i = 0; i < menuMeta.list.length; ++i) {
        initialMenuItems.push({
          id: menuMeta.list[i].id || menuMeta.list[i].name,
          keliname: menuMeta.list[i].name,
          menuSection: menuMeta.list[i].region,
          hotkeys: (menuMeta.list[i].hotkey && (Array.isArray(menuMeta.list[i].hotkey) ? menuMeta.list[i].hotkey : [menuMeta.list[i].hotkey])),
          dependsOnBrowse: menuMeta.list[i].dependsonbrowse,
          browseSettled: this.$._t.panelControlsDataSettled[menuMeta.list[i].dependsonbrowse]
        });
      }
      this.$._t.$.menu?.updateMenuItems(initialMenuItems);
    }
  }

  async createRelatedLinksMenuControls(templateInstance) {
    if (this.disablePanelMenu) {
      return;
    }
    const relatedLinksMeta = await this.runKeliFunctionIn(templateInstance, 'getRelatedLinksMenuItems');

    const relatedMenuItems = [];
    if (relatedLinksMeta) {
      for (let i = 0; i < relatedLinksMeta.list.length; ++i) {
        relatedMenuItems.push({
          id: relatedLinksMeta.list[i].id,
          keliname: relatedLinksMeta.list[i].name,
          caption: relatedLinksMeta.list[i].caption,
          menuSection: relatedLinksMeta.list[i].region,
          extraEventProperties: relatedLinksMeta.list[i].related_link
        });
      }
      this.$._t.$.menu?.updateMenuItems(relatedMenuItems);
    }
  }

  async updateMenuItem(event, templateInstance, objectName) {
    if (this.disablePanelMenu) {
      return;
    }
    const itemDefinition = [];
    const visible = (event.value !== false);
    if (!visible) {
      itemDefinition.push({
        id: objectName,
        visible
      });
    } else {
      const newItemMeta = await this.runKeliFunctionIn(templateInstance, 'getMenuItemForUI', objectName);
      if (newItemMeta) {
        itemDefinition.push({
          id: newItemMeta.id || newItemMeta.name,
          keliname: newItemMeta.name,
          menuSection: newItemMeta.region,
          hotkeys: (newItemMeta.hotkey && (Array.isArray(newItemMeta.hotkey) ? newItemMeta.hotkey : [newItemMeta.hotkey])),
          visible: (event.value || event.value === undefined),
          dependsOnBrowse: newItemMeta.dependsonbrowse,
          browseSettled: this.$._t.panelControlsDataSettled[newItemMeta.dependsOnBrowse]
        });
      }
    }

    this.$._t.$.menu?.updateMenuItems(itemDefinition);
  }

  async saveAllFields(event, isClosingPanel) {
    isClosingPanel = (typeof isClosingPanel === 'boolean' ? isClosingPanel : false);

    if (this._combinedMeta && this._combinedMeta) {
      const keliAcceptPromises = new Set();
      for (let metaKey in this._combinedMeta) {
        if (!this._combinedMeta.hasOwnProperty(metaKey)) {
          continue;
        }
        let meta = this._combinedMeta[metaKey];
        if (meta.control?.element?._keliAccept) {
          if (!isClosingPanel && meta.control?.element?.focused) {
            continue;
          }
          keliAcceptPromises.add(meta.control.element._keliAccept(event, isClosingPanel));
        }
      }
      await Promise.all(keliAcceptPromises);
    }
  }

  destroyPanel() {
    microTask.run(() => {
      if (this._combinedMeta && this._combinedMeta) {
        for (const meta in this._combinedMeta) {
          if (!this._combinedMeta.hasOwnProperty(meta)) {
            continue;
          }
          if (meta.control && meta.control.element && meta.control.element.parentNode) {
            meta.control.element.empty?.();
            meta.control.element.remove?.();
            meta.control.element = null;
          }
        }
      }
      microTask.run(() => {
        this.empty?.();
        this.remove?.();
      });
    });
  }

  registerDefaultPanelRegions() {
    const existing = this.$._t.querySelectorAll('[keliname]');
    for (let i = 0; i < existing.length; ++i) {
      const keliName = existing[i].getAttribute('keliname');
      if (!this._regions[keliName]) {
        this._regions[keliName] = existing[i];
      }
    }
  }

  async createInitialControls(templateInstance) {
    const controlsMetadata = await this.runKeliFunctionIn(templateInstance, 'getDefaultDynamicControls');
    if (!this.isConnected) return;

    const configurableObjectTypes = ['field', 'bu', 'chart', 'checklist', 'discussion', 'table', 'map'];
    const configurableObjectNameBlackList = ['browse'];

    Object.assign(this._combinedMeta, controlsMetadata.map);

    let previousControl = null;
    for (const meta of controlsMetadata.list) {
      meta.name = meta.name?.toLowerCase();

      // Check for any vis changes that came in before we got our controls back
      if (this._pendingControlVisibilityChangesBeforeCreation[meta.name] !== undefined) {
        meta.vis = this._pendingControlVisibilityChangesBeforeCreation[meta.name];
      }

      // Add every control to a list to allow the user to customize their panels.
      if (meta.initialvisibility !== false && configurableObjectTypes.includes(meta.type) && 
          !configurableObjectNameBlackList.includes(meta.name) && meta.allowcontrolconfiguration !== false) {
        this.$._t.push('allAvailableControls', {
          keliname: meta.name,
          checked: (meta.vis !== false)
        });
      }

      // Skip if it's already been created
      if (this._combinedMeta[meta.name] && this._combinedMeta[meta.name].control) {
        continue;
      }

      this._combinedMeta[meta.name].index = meta.position || meta.defaultposition;

      // Create the control
      const control = this.createControlElement(meta, previousControl);
      if (control) {
        this._combinedMeta[meta.name].control = control;

        // Register the control as a region
        if (control.isRegion) {
          this._regions[meta.name] = control.element;
        }

        // Append the element if it exists
        if (control.element) {
          if (control.slot) {
            control.element.setAttribute('slot', control.slot);
          }
          control.regionElement.appendChild(control.element);
        }

        previousControl = control;
      }
    }
  }

  createControlElement(meta, previousControl) {
    const componentMeta = KeliInterface.registeredKeliControlTypes[meta.controltype];
    if (!componentMeta) {
      return;
    }

    let element;
    const visible = (meta.vis !== false);
    const isBlock = !!(!meta.inline && componentMeta.isBlockPanelElement);
    const isRegion = !!(componentMeta.isRegionElement);
    let regionName;

    if (visible) {
      element = document.createElement(componentMeta.tagName);
      element.createdFromPanelController = true; // This is used to manage AdjacentElement for the panel.
      element.id = meta.id || meta.name;
      element.keliname = meta.name;
      if (element.needsDataLoaded) {
        this._needsDataLoadedElements.add(element);
        this._needsAllDataLoadedEvent.add(element);
      }
      if (meta.inline) {
        element.classList.add('inline-with-columns');
      }
      if (isBlock) {
        element.classList.add('block-panel-element');
      }

      if (['bu'].includes(meta.type) && meta.hotkey) {
        element.hotkeys = (meta.hotkey && (Array.isArray(meta.hotkey) ? meta.hotkey : [meta.hotkey]));
      }

      meta.region = meta.region?.toLowerCase?.();

      if (['bu'].includes(meta.type) && meta.region === 'toolbar') {
        element.removeFromPanelTabOrder = true;
        element.fontScale = .8;
        element.focusOnActivation = false;
      }

      if (['table', 'datatable'].includes(meta.type) && !['browseobjects', 'fullpanel', 'footerbar'].includes(meta.region)) {
        element.resizableByUser = true;
      }

      if (['table', 'datatable'].includes(meta.type) && this.disableBrowseConfiguration) {
        element.disableBrowseConfiguration = true;
      }

      if (meta.type === 'bu') {
        element.dependsOnBrowse = meta.dependsonbrowse;
        element.browseSettled = this.$._t.panelControlsDataSettled[meta.dependsonbrowse];
      }
    }

    if (meta.name !== meta.region) {
      regionName = meta.region;
    }

    const control = {
      element,
      isBlock,
      regionName,
      isRegion
    };
    control.regionElement = this.determineControlRegion(control, previousControl);
    return control;
  }

  determineControlRegion(control, previousControl) {
    let region;
    let preventAutomaticSection = false;
    if (control.regionName === 'editobjects') {
      preventAutomaticSection = true;
    } else if (!control.regionName) {
      control.regionName = 'editobjects';
    }

    if (this._regions[control.regionName]) {
      region = this._regions[control.regionName];
      if (Array.isArray(region)) {
        region = region[region.length - 1];
      }
    }

    if (!region && control.regionName?.includes('.')) {
      const parts = control.regionName.split('.');
      const slotName = parts.pop();
      const controlName = parts.join('.');
      if (this._combinedMeta[controlName]?.control) {
        region = this._combinedMeta[controlName].control.element;
        control.slot = slotName;
      }

    }

    if (!region && this._combinedMeta[control.regionName]?.control) {
      region = this._combinedMeta[control.regionName].control.element;
    }

    // Look for regions with sub-regions
    if (!region && control.regionName?.includes('_')) {
      const parts = control.regionName.split('_');
      let slotName = parts.pop();
      const controlName = parts.join('_');
      if (slotName && !isNaN(parseInt(slotName, 10))) {
        slotName = window.numberToWords.toWords(slotName) || slotName;
      }
      if (this._regions[controlName]?.containsRegions) {
        region = this._regions[controlName];
        control.slot = slotName;
      }
    }

    if (region?.removeChildrenFromPanelTabOrder && control.element) {
      control.element.removeFromPanelTabOrder = true;
      control.element.focusOnActivation = false;
    }

    // Default to fields if we can't find the region it belongs to.
    if (!region) {
      region = this.$.fields;
    }

    // Remove block level controls from sections.
    if (control.isBlock && region.tagName === 'KELI-SECTION') {
      if (this._combinedMeta[region.keliname] && this._combinedMeta[region.keliname].control.regionElement) {
        if (this._combinedMeta[region.keliname].control.slot) {
          control.slot = this._combinedMeta[region.keliname].control.slot;
        }
        region = this._combinedMeta[region.keliname].control.regionElement;
      } else {
        region = this.$.fields;
      }
    }

    if (!control.isBlock) {
      let autoSection;

      // Create a new region if the previous control was a block element
      if (region.tagName === 'KELI-SECTION' && previousControl?.isBlock && control.regionName === previousControl.regionName) {
        autoSection = this.appendNewAutomaticRegion(control.regionName);
      }

      // If it's going into fields, and the last element in fields is an auto-generated section, add it. Otherwise, create a new section?
      // Create a new region for inline controls going into the default
      if (region === this.$.fields && !preventAutomaticSection) {
        if (this.$.fields.lastElementChild && this.$.fields.lastElementChild._autoGeneratedSection) {
          autoSection = this.$.fields.lastElementChild;
        } else {
          autoSection = this.appendNewAutomaticRegion();
        }
      }
      if (autoSection) {
        region = autoSection;
      }
    }

    return region;
  }

  appendNewAutomaticRegion(originalRegion) {
    const section = document.createElement('keli-section');
    section._autoGeneratedSection = true;
    section.classList.add('block-panel-element');

    ++this._automaticSectionCounter;
    section.id = `automatic_section_${this._automaticSectionCounter}`;

    if (originalRegion && this._regions[originalRegion]) {
      if (!Array.isArray(this._regions[originalRegion])) {
        this._regions[originalRegion] = [this._regions[originalRegion]];
      }
      this._regions[originalRegion].push(section);
    } else {
      this._regions[section.id] = section;
    }

    this.$.fields.appendChild(section);
    return section;
  }

  findClosestTrailingElement(meta) {
    const regionControls = [];
    for (const name in this._combinedMeta) {
      if (!this._combinedMeta.hasOwnProperty(name)) {
        continue;
      }
      if (this._combinedMeta[name].control.regionElement === meta.control.regionElement) {
        regionControls.push(this._combinedMeta[name]);
      }
    }

    let closestMeta;
    let currentClosestIndex;
    for (let i = 0; i < regionControls.length; ++i) {
      const regionMeta = regionControls[i];
      if (!regionMeta.control.element) {
        continue;
      }
      if (regionMeta.index > meta.index && (!currentClosestIndex || regionMeta.index < currentClosestIndex)) {
        currentClosestIndex = regionMeta.index;
        closestMeta = regionMeta;
      }
    }

    let closestTrailingElement;
    if (closestMeta) {
      // If it's a block element, go up the chain of containers.
      let element = closestMeta.control.element;
      if (meta.control.isBlock) {
        while (element.parentNode) {
          if (element.parentNode === this.$.fields) {
            closestTrailingElement = element;
            break;
          }
          element = element.parentNode;
        }

      } else {
        closestTrailingElement = element;
      }
    }

    return closestTrailingElement;
  }
}

customElements.define(KeliPanelController.is, KeliPanelController);
export {KeliPanelController};
